import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { fetchAllPages } from '../api/client';
import { useApi } from '../api/context';
import { PAGE_SIZE, queryKeys } from '../api/queries';
import type { DeploymentStatus, NetworkStatus } from '../api/types';
import { usePermission } from '../auth/context';
import { deploymentStatusPresentation, type Tone } from '../components/presentation';
import { networkStatusPresentation } from '../networks/shared';
import { strings } from '../strings';
import { MenuButton, type MenuEntry } from './Menu';
import { runLabel, useRouteEntities, type RouteEntities } from './routeContext';

const s = strings.shell;

const TONE_COLOR: Record<Tone, string> = {
  neutral: 'var(--text-faint)',
  info: 'var(--accent)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  orange: 'var(--replace)',
  danger: 'var(--danger)',
};

/** A status dot: an image named by the status, never text (spec 6.1.7). Networks have the same four statuses. */
export function StatusDot({ status }: { status: DeploymentStatus | NetworkStatus }) {
  const presentation = deploymentStatusPresentation[status];
  return (
    <span
      role="img"
      aria-label={s.statusDot(presentation.label)}
      className="status-dot"
      style={{ background: TONE_COLOR[presentation.tone] }}
    />
  );
}

function loadingEntries(isError: boolean): MenuEntry[] {
  return [{ kind: 'note', id: 'loading', label: isError ? s.loadFailed : s.loadingMenu }];
}

function Crumb({ children, current }: { children: ReactNode; current?: boolean }) {
  return (
    <li className="flex min-w-0 items-center">
      {current ? (
        <span className="crumb truncate font-semibold" aria-current="page">
          {children}
        </span>
      ) : (
        children
      )}
    </li>
  );
}

function SwitcherContent({ kind, children }: { kind: string; children?: ReactNode }) {
  return (
    <>
      <span className="crumb-kind">{kind}</span>
      {children}
      <ChevronDown aria-hidden className="size-3.5 shrink-0 text-fg-muted" />
    </>
  );
}

function BlueprintSwitcher({
  id,
  name,
  current,
}: {
  id: string;
  name: string | undefined;
  current: boolean;
}) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const list = useQuery({
    queryKey: queryKeys.blueprints(null),
    queryFn: () => api.listBlueprints({ limit: PAGE_SIZE, cursor: null }),
    enabled: open,
  });
  const entries: MenuEntry[] = list.data
    ? [
        ...list.data.items.map<MenuEntry>((b) => ({
          kind: 'link',
          id: b.id,
          label: b.name,
          to: `/blueprints/${b.id}`,
          current: b.id === id,
        })),
        { kind: 'separator', id: 'sep' },
        { kind: 'link', id: 'all', label: s.allBlueprints, to: '/blueprints' },
      ]
    : loadingEntries(list.isError);
  return (
    <MenuButton
      buttonClassName="crumb"
      buttonLabel={name ? `${s.kind.blueprint} ${name}` : s.kind.blueprint}
      buttonContent={
        <SwitcherContent kind={s.kind.blueprint}>
          {name && <b className={`truncate ${current ? '' : 'font-semibold'}`}>{name}</b>}
        </SwitcherContent>
      }
      menuLabel={s.kind.blueprint}
      entries={entries}
      onOpenChange={setOpen}
    />
  );
}

function DeploymentSwitcher({ entities }: { entities: RouteEntities }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const { deployment, blueprintId } = entities;
  const list = useQuery({
    queryKey: queryKeys.allDeployments(blueprintId ?? ''),
    queryFn: () => fetchAllPages((page) => api.listDeployments(blueprintId!, page)),
    enabled: open && blueprintId !== null,
  });
  const entries: MenuEntry[] = list.data
    ? [
        ...(list.data.length === 0
          ? [{ kind: 'note', id: 'none', label: s.noDeployments } satisfies MenuEntry]
          : list.data.map<MenuEntry>((d) => ({
              kind: 'link',
              id: d.id,
              label: d.name,
              icon: <StatusDot status={d.status} />,
              to: `/deployments/${d.id}`,
              current: d.id === deployment?.id,
            }))),
        { kind: 'separator', id: 'sep' },
        { kind: 'link', id: 'all', label: s.allDeployments, to: `/blueprints/${blueprintId}/deployments` },
      ]
    : loadingEntries(list.isError);
  const status = deployment ? deploymentStatusPresentation[deployment.status].label : null;
  return (
    <MenuButton
      buttonClassName="crumb"
      buttonLabel={deployment ? `${s.kind.deployment} ${deployment.name}, ${status}` : s.kind.deployment}
      buttonContent={
        <SwitcherContent kind={s.kind.deployment}>
          {deployment && (
            <>
              <StatusDot status={deployment.status} />
              <b className="truncate font-semibold">{deployment.name}</b>
            </>
          )}
        </SwitcherContent>
      }
      menuLabel={s.kind.deployment}
      entries={blueprintId ? entries : loadingEntries(false)}
      onOpenChange={setOpen}
    />
  );
}

function RunSwitcher({ entities }: { entities: RouteEntities }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const { run, deploymentId } = entities;
  const list = useQuery({
    queryKey: queryKeys.runs(deploymentId ?? '', null),
    queryFn: () => api.listRuns(deploymentId!, { limit: PAGE_SIZE, cursor: null }),
    enabled: open && deploymentId !== null,
  });
  const entries: MenuEntry[] = list.data
    ? [
        ...(list.data.items.length === 0
          ? [{ kind: 'note', id: 'none', label: s.noRuns } satisfies MenuEntry]
          : list.data.items.slice(0, 10).map<MenuEntry>((r) => ({
              kind: 'link',
              id: r.id,
              label: `${runLabel(r)} · ${strings.status.run[r.status]}`,
              to: `/runs/${r.id}`,
              current: r.id === run?.id,
            }))),
        { kind: 'separator', id: 'sep' },
        { kind: 'link', id: 'all', label: s.allRuns, to: `/runs?deployment=${deploymentId}` },
      ]
    : loadingEntries(list.isError);
  return (
    <MenuButton
      buttonClassName="crumb"
      buttonLabel={run ? `${s.kind.run} ${runLabel(run)}` : s.kind.run}
      buttonContent={
        <SwitcherContent kind={s.kind.run}>
          {run && <b className="truncate font-semibold">{runLabel(run)}</b>}
        </SwitcherContent>
      }
      menuLabel={s.kind.run}
      entries={deploymentId ? entries : loadingEntries(false)}
      onOpenChange={setOpen}
    />
  );
}

/** The Network switcher (feature spec 17): "Network ● Production ▾" with the first page of networks. */
function NetworkSwitcher({ entities }: { entities: RouteEntities }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const { network, networkId } = entities;
  const list = useQuery({
    queryKey: queryKeys.networks(null),
    queryFn: () => api.listNetworks({ limit: PAGE_SIZE, cursor: null }),
    enabled: open,
  });
  const n = strings.networks.nav;
  const entries: MenuEntry[] = list.data
    ? [
        ...(list.data.items.length === 0
          ? [{ kind: 'note', id: 'none', label: n.noNetworks } satisfies MenuEntry]
          : list.data.items.map<MenuEntry>((item) => ({
              kind: 'link',
              id: item.id,
              label: item.name,
              icon: <StatusDot status={item.status} />,
              to: `/networks/${item.id}`,
              current: item.id === networkId,
            }))),
        { kind: 'separator', id: 'sep' },
        { kind: 'link', id: 'all', label: n.allNetworks, to: '/networks' },
      ]
    : loadingEntries(list.isError);
  const status = network ? networkStatusPresentation[network.status].label : null;
  return (
    <MenuButton
      buttonClassName="crumb"
      buttonLabel={strings.networks.runs.networkSwitcher(network?.name ?? null, status)}
      buttonContent={
        <SwitcherContent kind={n.kind}>
          {network && (
            <>
              <StatusDot status={network.status} />
              <b className="truncate font-semibold">{network.name}</b>
            </>
          )}
        </SwitcherContent>
      }
      menuLabel={n.kind}
      entries={entries}
      onOpenChange={setOpen}
    />
  );
}

/** A network run's Run switcher: the network's 10 latest runs and "All runs" (`/runs?network=<id>`). */
function NetworkRunSwitcher({ entities }: { entities: RouteEntities }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const { run, networkId } = entities;
  const list = useQuery({
    queryKey: queryKeys.networkRuns(networkId ?? '', null),
    queryFn: () => api.listNetworkRuns(networkId!, { limit: PAGE_SIZE, cursor: null }),
    enabled: open && networkId !== null,
  });
  const entries: MenuEntry[] = list.data
    ? [
        ...(list.data.items.length === 0
          ? [{ kind: 'note', id: 'none', label: s.noRuns } satisfies MenuEntry]
          : list.data.items.slice(0, 10).map<MenuEntry>((r) => ({
              kind: 'link',
              id: r.id,
              label: `${runLabel(r)} · ${strings.status.run[r.status]}`,
              to: `/runs/${r.id}`,
              current: r.id === run?.id,
            }))),
        { kind: 'separator', id: 'sep' },
        { kind: 'link', id: 'all', label: s.allRuns, to: `/runs?network=${networkId}` },
      ]
    : loadingEntries(list.isError);
  return (
    <MenuButton
      buttonClassName="crumb"
      buttonLabel={run ? `${s.kind.run} ${runLabel(run)}` : s.kind.run}
      buttonContent={
        <SwitcherContent kind={s.kind.run}>
          {run && <b className="truncate font-semibold">{runLabel(run)}</b>}
        </SwitcherContent>
      }
      menuLabel={s.kind.run}
      entries={networkId ? entries : loadingEntries(false)}
      onOpenChange={setOpen}
    />
  );
}

export function CloudAccountName({ id }: { id: string }) {
  const api = useApi();
  const canOperate = usePermission('operate');
  const account = useQuery({
    queryKey: queryKeys.cloudAccount(id),
    queryFn: () => api.getCloudAccount(id),
    enabled: canOperate,
  });
  return <>{account.data?.data.name ?? ''}</>;
}

/** The breadcrumb of spec 6.1.7, built from the route. */
export function Breadcrumbs() {
  const entities = useRouteEntities();
  const { context, blueprint, blueprintId } = entities;
  const items: ReactNode[] = [];

  const blueprintCrumb = (current: boolean) =>
    blueprintId && (
      <Crumb key="blueprint">
        <BlueprintSwitcher id={blueprintId} name={blueprint?.name} current={current} />
      </Crumb>
    );

  switch (context.page) {
    case 'blueprints':
      items.push(
        <Crumb key="list" current>
          {s.pages.blueprints}
        </Crumb>,
      );
      break;
    case 'designer':
    case 'versions':
    case 'deployments':
      items.push(blueprintCrumb(false));
      items.push(
        <Crumb key="page" current>
          <span className="crumb-kind">{s.pages[context.page]}</span>
        </Crumb>,
      );
      break;
    case 'deployment':
      items.push(blueprintCrumb(false));
      items.push(
        <Crumb key="deployment">
          <DeploymentSwitcher entities={entities} />
        </Crumb>,
      );
      break;
    case 'run':
      if (entities.run && entities.networkId) {
        items.push(
          <Crumb key="network">
            <NetworkSwitcher entities={entities} />
          </Crumb>,
        );
        items.push(
          <Crumb key="run">
            <NetworkRunSwitcher entities={entities} />
          </Crumb>,
        );
        break;
      }
      items.push(blueprintCrumb(false));
      items.push(
        <Crumb key="deployment">
          <DeploymentSwitcher entities={entities} />
        </Crumb>,
      );
      items.push(
        <Crumb key="run">
          <RunSwitcher entities={entities} />
        </Crumb>,
      );
      break;
    case 'all-deployments':
      items.push(
        <Crumb key="list" current>
          {s.pages.allDeployments}
        </Crumb>,
      );
      break;
    case 'runs':
      items.push(
        <Crumb key="list" current>
          {s.pages.runs}
        </Crumb>,
      );
      break;
    case 'networks':
      items.push(
        <Crumb key="list" current>
          {strings.networks.nav.networks}
        </Crumb>,
      );
      break;
    case 'network':
      items.push(
        <Crumb key="network">
          <NetworkSwitcher entities={entities} />
        </Crumb>,
      );
      break;
    case 'cloud-accounts':
      items.push(
        <Crumb key="list" current>
          {s.pages.cloudAccounts}
        </Crumb>,
      );
      break;
    case 'cloud-account':
      items.push(
        <Crumb key="list">
          <Link className="crumb" to="/cloud-accounts">
            {s.pages.cloudAccounts}
          </Link>
        </Crumb>,
      );
      items.push(
        <Crumb key="account" current>
          <CloudAccountName id={context.cloudAccountId} />
        </Crumb>,
      );
      break;
    case 'about':
    case 'whats-new':
      items.push(<Crumb key="help">{<span className="crumb text-fg-muted">{s.pages.help}</span>}</Crumb>);
      items.push(
        <Crumb key={context.page} current>
          {context.page === 'about' ? s.pages.about : s.pages.whatsNew}
        </Crumb>,
      );
      break;
    case 'other':
      break;
  }

  return (
    <nav aria-label={s.breadcrumb} className="min-w-0">
      <ol className="crumbs flex min-w-0 items-center">{items}</ol>
    </nav>
  );
}
